import type {FloatingContext} from '@floating-ui/react';
import {autoUpdate} from '@floating-ui/react';
import type {SetShown} from '../typedefs';
import {checkOcclusion} from '../utils/check-occlusion';
import type {RefObject} from 'react';
import {useRef} from 'react';
import {FLOATING_EVENT_VISIBILITY_HIDE} from '../constants';
import {useCallbackWithRef} from '@pb/kit-util-use-callback-with-ref';

/**
 * Util to hide the floating element on scroll.
 * Use in place of autoUpdate from floating-ui.
 * Autoupdate dy default fires update on scroll in all parents,
 * so we can use it to hide the floating element
 * Emits FLOATING_EVENT_VISIBILITY_HIDE event on context when hidden to allow other components to react (e.g. pause returnFocus)
 */
function updatePositionAndVisibility(
    setOpen: SetShown,
    contextRef: RefObject<FloatingContext | undefined>,
    hideOn: 'scroll' | 'occlusion' = 'scroll',
): typeof autoUpdate {
    return (referenceEl, floatingEl, update) => {
        return autoUpdate(
            referenceEl,
            floatingEl,
            (e?: unknown) => {
                update();

                if (
                    hideOn === 'occlusion'
                ) {
                    const anchorEl = 'contextElement' in referenceEl ?
                        referenceEl.contextElement :
                        referenceEl;

                    if (anchorEl && 'contains' in anchorEl) {
                        const isHidden = checkOcclusion(anchorEl);

                        if (isHidden) {
                            contextRef.current?.events.emit(FLOATING_EVENT_VISIBILITY_HIDE);
                            setOpen(false, e instanceof Event ? e : undefined, 'visibility');
                        }
                    }
                    return;
                }

                if (e instanceof Event && e.type === 'scroll') {
                    setOpen(false, e, 'scroll');
                }
            });
    };
}

export type UseUpdateOptions = {
    /**
     * Condition to hide the floating element
     * 'scroll' - hide on scroll (default)
     * 'occlusion' - hide when the anchor element is occluded (not visible)
     */
    hideOn?: 'scroll' | 'occlusion',
};

export const useUpdate = (
    setOpen: SetShown,
    options?: UseUpdateOptions,
) => {
    const contextRef = useRef<FloatingContext>(undefined);

    return {
        whileElementsMounted: useCallbackWithRef(
            updatePositionAndVisibility(setOpen, contextRef, options?.hideOn),
        ),
        contextRef,
    };
};
